import { ColorEnum } from '@/constants/color'
import { AutomaticContainer } from '@/features/pos/components/Cart/parts/Overview/parts/CartItem/types'
import IconButton from '@/features/ui/buttons/components/IconButton'
import DeleteIcon from '@/features/ui/icons/components/DeleteIcon'
import PlusIcon from '@/features/ui/icons/components/PlusIcon'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'

interface AutomaticContainerRowProps {
    container: AutomaticContainer
    action: 'remove' | 'restore'
    onAction: (containerId: string) => void
}

const AutomaticContainerRow: FC<AutomaticContainerRowProps> = ({ container, action, onAction }) => {
    const isMarkedForRemoval = action === 'restore'
    const { t } = useTranslation()

    return (
        <View style={[styles.container, isMarkedForRemoval && styles.containerRemoval]}>
            <View style={styles.description}>
                <Typography.Description
                    size="large"
                    font="medium"
                    numberOfLines={2}
                    style={[styles.name, isMarkedForRemoval && styles.nameRemoval]}
                >
                    {container.name}
                </Typography.Description>
                <Typography.Description
                    size="small"
                    font="medium"
                    numberOfLines={1}
                    style={[styles.price, isMarkedForRemoval && styles.priceRemoval]}
                >
                    {container.price}
                </Typography.Description>
            </View>
            <IconButton
                type={isMarkedForRemoval ? 'filled' : 'outline'}
                color={isMarkedForRemoval ? 'primary' : 'secondary'}
                size={44}
                Icon={isMarkedForRemoval ? PlusIcon : DeleteIcon}
                onPress={() => onAction(container.id)}
                style={styles.actionButton}
                accessibilityRole="button"
                accessibilityLabel={t(
                    isMarkedForRemoval
                        ? 'pos.orderFinalization.cart.editContainers.restore'
                        : 'pos.orderFinalization.cart.editContainers.remove',
                    { name: container.name }
                )}
                testID={`${action}-automatic-container-${container.id}`}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        minHeight: 60,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 8,
        paddingLeft: 12,
        paddingRight: 8,
        borderWidth: 1,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        backgroundColor: ColorEnum.GRAY_100,
    },
    containerRemoval: {
        borderWidth: 2,
        borderColor: ColorEnum.BRICK_600,
        backgroundColor: ColorEnum.Brick100,
    },
    description: {
        flex: 1,
        minWidth: 0,
        gap: 8,
    },
    name: {
        color: ColorEnum.Jet700,
    },
    nameRemoval: {
        color: ColorEnum.BRICK_600,
    },
    price: {
        color: ColorEnum.JET_500,
    },
    priceRemoval: {
        color: ColorEnum.BRICK_500,
    },
    actionButton: {
        borderRadius: 8,
    },
})

export default AutomaticContainerRow
